/**
 * Handles optimization for relative urls.
 *
 *
 * All resizing modes preserve aspect ratio. @see https://developers.cloudflare.com/images/image-resizing/url-format/#fit.
 *
 * @returns {string} - The optimized relative image source.
 * @param {string} imageSrc - The image url.
 * @param {CloudflareImageOptions} options
 */
function optimizeRelativeUrl(imageSrc, options) {
    const composedOptions = formatOptions(options);
    return [
        '/cdn-cgi/image',
        composedOptions,
        imageSrc,
    ].join('/');
}
const defaultCloudflareImageOptions = {
    format: 'avif',
    quality: 90,
    fit: 'scale-down',
    gravity: 'auto',
};
/**
 * Optimizes the image source.
 */
export function optimizeImageSrc(imageSrc, options = defaultCloudflareImageOptions) {
    const mergedOptions = Object.assign(Object.assign({}, defaultCloudflareImageOptions), options);
    try {
        if (imageSrc.startsWith('/')) {
            return optimizeRelativeUrl(imageSrc, mergedOptions);
        }
        return optimizeAbsoluteUrl(imageSrc, mergedOptions);
    }
    catch (e) {
        console.info(`Error while optimizing image url: ${imageSrc}`, e.message);
        return imageSrc !== null && imageSrc !== void 0 ? imageSrc : '';
    }
}
/**
 * Handles optimization for absolute urls.
 *
 *
 * @returns {string} - The optimized absolute image source.
 * @param {string} imageSrc - The image url.
 * @param {CloudflareImageOptions} options
 */
function optimizeAbsoluteUrl(imageSrc, options) {
    // eslint-disable-next-line
    const url = new URL(imageSrc.replace(/cdn-cgi\/image\/[^\/]+\//g, ""));
    const pathname = url.pathname;
    const protocol = url.protocol;
    const imageHost = removeHttp(url.hostname);
    const composedOptions = formatOptions(options);
    return protocol + '//' +
        [
            imageHost,
            'cdn-cgi/image',
            composedOptions,
            pathname.slice(1, pathname.length),
        ].join('/');
}
/**
 * Create Cloudflare composition Options.
 *
 *
 * @returns {string} - The composition string.
 * @param {CloudflareImageOptions} options
 */
const formatOptions = (options) => Object.entries(options)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .filter(([_, value]) => value !== 0 && value !== undefined)
    .map(([key, value]) => [`${key}=${value}`])
    .join(',');
/**
 * Removes the protocol from url.
 *
 * @param {string} url - The url.
 * @returns {string} - The url without protocol.
 */
const removeHttp = (url) => url.slice(url.indexOf('//') + 1);
//# sourceMappingURL=cloudflare.js.map